import React from 'react'
import Button from 'material-ui/Button'
import { withStyles } from 'material-ui/styles'
import Icon from 'material-ui/Icon'
import styles from './styles'

import Title from '../ui/title'
import Content from '../ui/content'
import Back from '../ui/back'
import Breadcrumbs from '../ui/breadcrumbs'

class SubscriptionDetail extends React.Component<any, any> {
  formatCurrency = (gbp) => {
    if (!gbp && gbp !== 0) return '£0.00'
    return `£${parseFloat(gbp).toFixed(2)}`
  }

  formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  getStatusColor = (status) => {
    const statusMap = {
      Active: '#4caf50',
      PastDue: '#ff9800',
      Pending: '#2196f3',
      Canceled: '#f44336',
      Failed: '#f44336'
    }
    return statusMap[status] || '#999'
  }

  render() {
    const { subscription, classes, onBack, translate } = this.props

    if (!subscription) {
      return <div>{translate ? translate('loading') : 'Loading...'}</div>
    }

    return (
      <div>
        <Title
          title={translate('subscription_details')}
          back={
            <Breadcrumbs
              items={[
                { label: translate('dashboard_title'), link: '/dashboard' },
                { label: subscription.organisationName, link: `/organisation-detail/${subscription.organisationId}` },
                { label: translate('subscription_details') }
              ]}
            />
          }
        />
        <Content translate={translate}>
          <div className={classes.container}>
            {/* Header Section */}
            <div className={classes.headerSection}>
              <div className={classes.headerTitle}>
                <h2 style={{ margin: 0 }}>{subscription.organisationName}</h2>
                <span style={{
                  backgroundColor: subscription.status === 'Active' ? '#e8f5e9' : '#fff3e0',
                  color: this.getStatusColor(subscription.status),
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'inline-block',
                  marginTop: '8px'
                }}>
                  {subscription.status}
                </span>
              </div>
            </div>

            {/* Main Details Grid */}
            <div className={classes.gridContainer}>
              {/* Billing Information */}
              <div className={classes.section}>
                <h3 className={classes.sectionTitle}>
                  <Icon style={{ marginRight: '8px', fontSize: '20px' }}>payment</Icon>
                  {translate('billing_information')}
                </h3>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('pricing_tier')}:</span>
                  <span className={classes.value}>{subscription.subscriptionTier}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('device_count')}:</span>
                  <span className={classes.value}>{subscription.deviceCount}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('monthly_price')}:</span>
                  <span className={classes.valueHighlight}>
                    {this.formatCurrency(subscription.monthlyPriceGBP)}
                  </span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('total_price')}:</span>
                  <span className={classes.valueHighlight}>
                    {this.formatCurrency(subscription.totalPriceGBP)}
                  </span>
                </div>
                {subscription.discountCode && (
                  <>
                    <div className={classes.detailRow}>
                      <span className={classes.label}>{translate('discount_code')}:</span>
                      <span className={classes.value}>{subscription.discountCode}</span>
                    </div>
                    <div className={classes.detailRow}>
                      <span className={classes.label}>{translate('discount_amount')}:</span>
                      <span className={classes.value}>
                        {this.formatCurrency(subscription.discountAmountGBP)}
                      </span>
                    </div>
                  </>
                )}
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('currency')}:</span>
                  <span className={classes.value}>{subscription.currentPrice.currency.toUpperCase()}</span>
                </div>
              </div>

              {/* Subscription Dates */}
              <div className={classes.section}>
                <h3 className={classes.sectionTitle}>
                  <Icon style={{ marginRight: '8px', fontSize: '20px' }}>date_range</Icon>
                  {translate('dates')}
                </h3>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('start_date')}:</span>
                  <span className={classes.value}>{this.formatDate(subscription.startDate)}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('current_period_start')}:</span>
                  <span className={classes.value}>
                    {this.formatDate(subscription.currentPeriodStart)}
                  </span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('current_period_end')}:</span>
                  <span className={classes.value}>
                    {this.formatDate(subscription.currentPeriodEnd)}
                  </span>
                </div>
                {subscription.canceledAt && (
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('canceled_at')}:</span>
                    <span className={classes.value}>
                      {this.formatDate(subscription.canceledAt)}
                    </span>
                  </div>
                )}
              </div>

              {/* Stripe Information */}
              <div className={classes.section}>
                <h3 className={classes.sectionTitle}>
                  <Icon style={{ marginRight: '8px', fontSize: '20px' }}>info</Icon>
                  {translate('stripe_information')}
                </h3>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('stripe_subscription_id')}:</span>
                  <span className={classes.valueMono}>{subscription.stripeSubscriptionId}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('stripe_customer_id')}:</span>
                  <span className={classes.valueMono}>{subscription.stripeCustomerId}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('stripe_seller_label')}:</span>
                  <span className={classes.value}>
                    {subscription.stripeSellerKey
                      ? `${subscription.sellerLegalName || ''} (${subscription.stripeSellerKey.toUpperCase()}${subscription.billingCountryCode ? `, ${subscription.billingCountryCode}` : ''})`
                      : '—'}
                  </span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('stripe_account_id')}:</span>
                  <span className={classes.valueMono}>
                    {subscription.stripeAccountId
                      ? `${subscription.stripeAccountId} (${subscription.stripeLiveMode ? 'live' : 'test'})`
                      : '—'}
                  </span>
                </div>
              </div>

              {/* Record Information */}
              <div className={classes.section}>
                <h3 className={classes.sectionTitle}>
                  <Icon style={{ marginRight: '8px', fontSize: '20px' }}>storage</Icon>
                  {translate('record_information')}
                </h3>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('subscription_id')}:</span>
                  <span className={classes.value}>{subscription.id}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('organisation_id')}:</span>
                  <span className={classes.value}>{subscription.organisationId}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('created_date')}:</span>
                  <span className={classes.value}>
                    {this.formatDate(subscription.createdDate)}
                  </span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('modified_date')}:</span>
                  <span className={classes.value}>
                    {this.formatDate(subscription.modifiedDate)}
                  </span>
                </div>
              </div>
            </div>

            {/* Event Logs Section */}
            {subscription.eventLogs && subscription.eventLogs.length > 0 && (
              <div className={classes.section} style={{ marginTop: '30px' }}>
                <h3 className={classes.sectionTitle}>
                  <Icon style={{ marginRight: '8px', fontSize: '20px' }}>history</Icon>
                  {translate('event_logs')}
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        <th>{translate('general_date')}</th>
                        <th>{translate('general_event')}</th>
                        <th>{translate('status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscription.eventLogs.map((log, index) => (
                        <tr key={index}>
                          <td>{this.formatDate(log.eventTimestamp)}</td>
                          <td>{log.eventType}</td>
                          <td>{log.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty State for Event Logs */}
            {(!subscription.eventLogs || subscription.eventLogs.length === 0) && (
              <div className={classes.emptyState} style={{ marginTop: '30px' }}>
                <Icon style={{ fontSize: '48px', color: '#ccc' }}>history</Icon>
                <p style={{ color: '#999' }}>{translate('no_event_logs')}</p>
              </div>
            )}
          </div>
        </Content>
      </div>
    )
  }
}

export default withStyles(styles)(SubscriptionDetail)
